// S-063-2 calendar_events Firestore 入出力（§4）
//
// users/{uid}/calendar_events に §1 スキーマの 7 フィールド（+ 容量調整用 seq）のみを書込み、
// batch 単位 / 全削除の導線と 200 件上限維持を集約する。Firestore I/O をここに閉じてテスト性を確保する。
//
// やらないこと: マッチング・Gemini 注入（S-063-3）、companyIds 更新、firestore.rules 変更（S-063-1）。

import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ParsedEvent, resolveEndTime } from './calendarParser';

// 件数上限（クライアント責務 / 共有契約 §1）。rules は件数を強制できないため超過分を古い順削除する。
export const MAX_CALENDAR_EVENTS = 200;

// Firestore に保存される 1 件のイベント（§1 スキーマ 7 フィールド + seq + createdAt/updatedAt）。
// S-063-4: 永続保存化に伴い createdAt（初回取込時刻）/ updatedAt（最終(再)書込み時刻）を型に明示する。
//   いずれも書込みは serverTimestamp。read 時は Firestore Timestamp。任意（旧 doc は updatedAt 無し）。
export interface SavedCalendarEvent {
  id: string;
  companyName: string;
  department: string;
  contactName: string;
  startTime: string; // +09:00
  endTime: string; // +09:00
  batchId: string;
  seq: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// UUID v4 生成（crypto.randomUUID 優先・無ければ簡易フォールバック）。
// ドキュメント ID / batchId 用。暗号強度は不要（衝突回避のみ）。
function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function eventsCol(uid: string) {
  return collection(db, 'users', uid, 'calendar_events');
}

// seq の単調増加を取込跨ぎで保証する（同一ミリ秒内の連続取込でも衝突させない / レビュー #8）。
let lastSeqBase = 0;

// 容量調整（§4.3）。新規書込みが完了した後に呼ぶ（createdAt pending-null を踏まないため）。
//   - 全件 getDocs しない: 件数は getCountFromServer で 1 read。
//   - 並び順は createdAt（pending で null になりうる）ではなく seq 昇順（クライアント単調増加）。
async function trimToCapacity(uid: string, max = MAX_CALENDAR_EVENTS): Promise<void> {
  const col = eventsCol(uid);
  const countSnap = await getCountFromServer(col);
  const overflow = countSnap.data().count - max;
  if (overflow <= 0) return;
  const oldSnap = await getDocs(query(col, orderBy('seq', 'asc'), limit(overflow)));
  await Promise.all(oldSnap.docs.map((d) => deleteDoc(d.ref)));
}

// 書込みフロー（§4.4）。失敗行（会社名空 / 時刻空）は除外し、§1 の 7 フィールド + seq のみを書く。
//   返り値 saved===0 のときは batchId='' を返し、呼び出し側は成功トーストを出さない（§5.2 / P2-8）。
export async function saveEvents(
  uid: string,
  events: ParsedEvent[],
): Promise<{ saved: number; batchId: string }> {
  const writable = events.filter((e) => e.companyName && e.startTime);
  if (writable.length === 0) return { saved: 0, batchId: '' };

  const batchId = genId();
  // 取込単位の単調増加基準（§4.3 seq）。前回基準より必ず大きい値にして同一 ms 連続取込でも衝突させない。
  const baseSeq = Math.max(Date.now(), lastSeqBase + 1);
  lastSeqBase = baseSeq + writable.length;
  let saved = 0;

  // (1) まず書き込む（pending を残さない順序。容量調整は後）。
  for (const e of writable) {
    const eventId = genId();
    await setDoc(doc(db, 'users', uid, 'calendar_events', eventId), {
      companyName: e.companyName.slice(0, 100),
      department: (e.department || '').slice(0, 100),
      contactName: (e.contactName || '').slice(0, 60),
      startTime: e.startTime, // +09:00（filter 済みで非 null）
      endTime: resolveEndTime(e.startTime as string, e.endTime),
      batchId,
      seq: baseSeq + saved, // クライアント単調増加（容量調整の並び順用）
      // S-063-4 / DEC-024 改訂 (a): 永続保存化（旧 7日 TTL 廃止）。createdAt=初回取込時刻。
      createdAt: serverTimestamp(),
      // S-063-4: 再取込上書き(S-063-9)の鮮度管理用。初回作成時は createdAt と同時刻、
      //   再取込で同一 doc を上書きする際に最新書込み時刻へ更新される（rules では optional・timestamp）。
      updatedAt: serverTimestamp(),
    });
    saved++;
  }

  // (2) 書込み後に容量調整（createdAt pending を踏まず、seq 昇順で最古から削除）。
  await trimToCapacity(uid);
  return { saved, batchId };
}

// ─────────────────────────────────────────────────────────────────────────────
// S-063-9: 再取込の重複検出＋上書き（同一時間帯の既存予定 → 新しい方を優先）。
//   永続保存（S-063-4）により予定が残るため、再取込時に「同じ時間帯」の既存予定があれば
//   重複として検出し、ユーザー確認のうえ既存 doc を新しい内容で上書きして鮮度を担保する。
//   - 「同じ時間帯」= 取込予定の [start,end) と既存予定の [start,end) が重なる（overlap）。
//   - 内容が完全一致 → identical（重複作成しない・確認不要）。内容相違 → conflict（上書き確認 UI へ）。
//   - 上書きは既存 doc id / batchId / seq / createdAt を維持し、内容＋updatedAt のみ更新（鮮度）。
//     rules update 許可境界（validCalendarEvent・S-063-4）を満たす（merge:true で createdAt 等は保持）。
// ─────────────────────────────────────────────────────────────────────────────

// 保存形（書込み時の正規化後）の内容フィールド。identical 判定と上書きペイロードで共用する。
interface NormalizedContent {
  companyName: string;
  department: string;
  contactName: string;
  startTime: string;
  endTime: string;
}

// ParsedEvent（writable 前提＝companyName/startTime あり）を保存形に正規化する（saveEvents と同一変換）。
function normalizeContent(e: ParsedEvent): NormalizedContent {
  return {
    companyName: e.companyName.slice(0, 100),
    department: (e.department || '').slice(0, 100),
    contactName: (e.contactName || '').slice(0, 60),
    startTime: e.startTime as string, // writable filter 済みで非 null
    endTime: resolveEndTime(e.startTime as string, e.endTime),
  };
}

// ISO(+09:00) → epoch ms。失敗時 NaN。overlap 判定用。
function isoMs(iso: string): number {
  return iso ? Date.parse(iso) : NaN;
}

// 2 区間 [aStart,aEnd) と [bStart,bEnd) が重なるか（端点接触は重なりとしない）。
function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  if ([aStart, aEnd, bStart, bEnd].some((n) => Number.isNaN(n))) return false;
  return aStart < bEnd && bStart < aEnd;
}

// 上書き対象 1 件（既存 doc を incoming の内容で更新する）。
export interface EventOverwrite {
  existingId: string; // 上書きする既存 doc id（id/batchId/seq/createdAt は維持）
  existing: SavedCalendarEvent; // 確認 UI の「現在」表示用
  incoming: ParsedEvent; // 新しい予定（こちらを優先）
  content: NormalizedContent; // 書込みに使う正規化済み内容
}

// 再取込の振り分け結果。
export interface ReimportSplit {
  fresh: ParsedEvent[]; // 同時間帯の既存なし → 通常保存（saveEvents）
  conflicts: EventOverwrite[]; // 同時間帯あり＋内容相違 → 上書き確認 UI
  identicalCount: number; // 同時間帯あり＋内容一致 → 何もしない（重複作成しない）
}

// 取込予定を既存予定（saved）と突き合わせ、fresh / conflicts / identical に振り分ける（純関数・I/O なし）。
//   1 つの既存 doc は最大 1 件の incoming にしか割り当てない（consumed 管理）。同 startTime を優先採用。
export function splitReimport(
  incoming: ParsedEvent[],
  existing: SavedCalendarEvent[],
): ReimportSplit {
  const writable = incoming.filter((e) => e.companyName && e.startTime);
  // 既存予定を epoch 区間に展開（無効時刻は overlap 判定で除外される）。
  const exRanges = existing.map((ev) => ({
    ev,
    start: isoMs(ev.startTime),
    end: isoMs(ev.endTime),
    consumed: false,
  }));
  const split: ReimportSplit = { fresh: [], conflicts: [], identicalCount: 0 };

  for (const inc of writable) {
    const content = normalizeContent(inc);
    const inStart = isoMs(content.startTime);
    const inEnd = isoMs(content.endTime);
    // 同 startTime 完全一致を最優先、無ければ最初に重なる未消費の既存を採用。
    const hit =
      exRanges.find((r) => !r.consumed && r.ev.startTime === content.startTime) ||
      exRanges.find((r) => !r.consumed && rangesOverlap(inStart, inEnd, r.start, r.end));
    if (!hit) {
      split.fresh.push(inc);
      continue;
    }
    hit.consumed = true;
    const same =
      hit.ev.companyName === content.companyName &&
      hit.ev.department === content.department &&
      hit.ev.contactName === content.contactName &&
      hit.ev.startTime === content.startTime &&
      hit.ev.endTime === content.endTime;
    if (same) {
      split.identicalCount += 1; // 既に最新＝重複作成も上書きもしない
    } else {
      split.conflicts.push({ existingId: hit.ev.id, existing: hit.ev, incoming: inc, content });
    }
  }
  return split;
}

// 承認された上書きを既存 doc に in-place 反映する（新しい方を優先）。
//   id/batchId/seq/createdAt は触らず、内容フィールド＋updatedAt のみを merge 更新（鮮度担保・S-063-9）。
//   merge:true により更新後 doc も完全な正規イベント（rules validCalendarEvent を満たす）。
export async function overwriteEvents(uid: string, overwrites: EventOverwrite[]): Promise<number> {
  let updated = 0;
  for (const o of overwrites) {
    if (!o.existingId || !o.content.companyName || !o.content.startTime) continue;
    await setDoc(
      doc(db, 'users', uid, 'calendar_events', o.existingId),
      {
        companyName: o.content.companyName,
        department: o.content.department,
        contactName: o.content.contactName,
        startTime: o.content.startTime,
        endTime: o.content.endTime,
        // S-063-9: 再(再)書込み時刻。createdAt（初回取込時刻）は維持され updatedAt のみ前進する。
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    updated += 1;
  }
  return updated;
}

// この取込（batchId）を取り消す（§4.5 batch 単位削除）。
export async function deleteBatch(uid: string, batchId: string): Promise<void> {
  if (!batchId) return;
  const snap = await getDocs(query(eventsCol(uid), where('batchId', '==', batchId)));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}

// 保存済み予定をすべて削除（§4.5 全削除・プライバシー配慮）。
export async function deleteAllEvents(uid: string): Promise<void> {
  const snap = await getDocs(eventsCol(uid));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}
